import { COMPANY_INFO, SERVICE_AREAS_DATA } from "../../../mocks/aegis-data";

interface ServiceAreasProps {
  title?: string;
  subtitle?: string;
  highlighted?: string;
}

const COUNTIES = [
  {
    name: "Sarasota County",
    note: "Primary service county",
    detail: "Custom homes, remodels & structural work",
    icon: "ri-map-pin-2-fill",
  },
  {
    name: "Manatee County",
    note: "Bradenton, Lakewood Ranch & AMI",
    detail: "Northern coverage including Anna Maria Island",
    icon: "ri-map-pin-2-fill",
  },
  {
    name: "Charlotte County",
    note: "Englewood, North Port & beyond",
    detail: "Southern coverage to Charlotte County",
    icon: "ri-map-pin-2-fill",
  },
];

export default function ServiceAreas({
  title = "Service Areas Across Southwest Florida",
  subtitle = "We build and remodel throughout Sarasota, Manatee, and Charlotte counties — including every Gulf Coast barrier island.",
  highlighted,
}: ServiceAreasProps) {
  return (
    <section className="bg-aegisCream py-28">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end mb-14">
          <div>
            <div className="flex items-center gap-4 mb-5">
              <div className="h-px w-10 bg-aegisGold" />
              <span className="font-inter text-xs tracking-[0.2em] uppercase text-aegisGold">Where We Build</span>
            </div>
            <h2 className="font-cormorant font-semibold text-aegisDark leading-[1.1]" style={{ fontSize: "clamp(2.2rem, 3.5vw, 3.4rem)" }}>
              {title}
            </h2>
          </div>
          <div className="lg:pl-8 lg:border-l lg:border-aegisStone">
            <p className="font-inter text-aegisMuted text-base leading-relaxed">
              {subtitle}
            </p>
          </div>
        </div>

        {/* County cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-aegisStone mb-px">
          {COUNTIES.map((c) => (
            <div key={c.name} className="bg-white hover:bg-aegisCream transition-colors duration-200 group cursor-default px-8 py-7 flex items-start gap-5">
              <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 border border-aegisGold/30 group-hover:border-aegisGold bg-white transition-colors duration-200">
                <i className={`${c.icon} text-aegisGold text-xl`} />
              </div>
              <div>
                <h3 className="font-cormorant font-semibold text-aegisDark text-xl leading-tight">{c.name}</h3>
                <p className="font-inter text-aegisGold text-xs tracking-wide mt-0.5">{c.note}</p>
                <p className="font-inter text-aegisMuted text-sm mt-2">{c.detail}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Map — full prominence */}
        <div className="overflow-hidden">
          {/* Map header */}
          <div className="bg-aegisDark px-7 py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-2-fill text-aegisGold text-sm" />
              </div>
              <span className="font-inter text-sm text-white/80 font-medium">
                Aegis Construction Services
              </span>
            </div>
            <span className="font-inter text-xs text-white/40">4430 Ashton Rd, Sarasota FL 34233</span>
            <span className="ml-auto font-inter text-xs text-aegisGold hidden md:block">
              CGC1524141 · (941) 487-0007
            </span>
          </div>

          <div className="w-full" style={{ height: "520px" }}>
            <iframe
              src={COMPANY_INFO.mapEmbed}
              width="100%"
              height="100%"
              style={{ border: 0, display: "block" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Aegis Construction Services Sarasota FL Location Map"
            />
          </div>

          {/* Map footer */}
          <div className="bg-white border border-aegisStone border-t-0 px-7 py-5">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-water-flash-line text-aegisGold text-sm" />
                </div>
                <p className="font-inter text-aegisMuted text-xs">
                  Barrier Islands: Casey Key · Siesta Key · Longboat Key · Anna Maria Island · Lido Key · Bird Key
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Cities grid */}
        <div className="mt-px bg-white border border-aegisStone p-8">
          <p className="font-inter text-xs tracking-[0.18em] uppercase text-aegisMuted mb-6 pb-4 border-b border-aegisStone">
            Communities We Serve
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-2">
            {SERVICE_AREAS_DATA.map((area) => (
              <div
                key={area}
                className={`px-3 py-2.5 text-center border transition-colors duration-200 cursor-default hover:border-aegisGold hover:bg-aegisCream ${
                  highlighted === area
                    ? "border-aegisGold bg-aegisGold/8 text-aegisDark"
                    : "border-aegisStone text-aegisMuted hover:text-aegisDark"
                }`}
              >
                <span className="font-inter text-xs whitespace-nowrap">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
